/**
 * Beyond Measure — comprehensive standard Terms & Conditions.
 * Used as the seed for `settings.default_contract_terms` and exposed via the
 * "Load Beyond Measure standard T&Cs" button in Settings → Contract terms.
 *
 * Stored and rendered as HTML; the rich-text editor (TipTap) handles the
 * formatting (bold, underline, lists, headings).
 */
export const BM_DEFAULT_CONTRACT_TERMS_HTML = `<h1>1. SERVICES &amp; SCOPE</h1>
<h2>1.1 Services Provided.</h2>
<p>Beyond Measure Ltd ("Beyond Measure", "we", "us") will provide the development, deployment and support services for the custom platform modules selected in the accepted proposal ("the Platform") to the Client.</p>
<h2>1.2 Bespoke Development.</h2>
<p>All modules are custom-built for the Client's specific requirements as documented in the agreed Statement of Work. The Platform will be deployed to infrastructure designated by the Client (or managed infrastructure provided by Beyond Measure on the Client's behalf).</p>
<h2>1.3 Deliverables.</h2>
<p>Beyond Measure will deliver the Platform in accordance with the timeline and specifications set out in the accepted proposal. Any material changes to scope require written agreement from both parties.</p>

<h1>2. FEES &amp; PAYMENT TERMS</h1>
<h2>2.1 Build Fees.</h2>
<p>The one-off build fee is as stated in the accepted commercial option and covers development, testing, deployment and initial training.</p>
<h2>2.2 Transition Fee.</h2>
<p>During the transition period (typically 3–6 months post-deployment), the Client will pay the monthly transition fee stated in the commercial option. This covers iteration, refinement, bug fixes and ongoing support during the bedding-in phase.</p>
<h2>2.3 Post-Handover Maintenance.</h2>
<p>Following the handover milestone, the Client may optionally retain Beyond Measure for ongoing maintenance, security updates, infrastructure monitoring and support at the monthly rate stated in the commercial option. This is optional; the Platform will continue to function if the maintenance retainer is not retained.</p>
<h2>2.4 Payment Schedule.</h2>
<ul>
<li>50% of the build fee is payable on contract signature</li>
<li>50% of the build fee is payable on go-live</li>
<li>Transition fees are invoiced monthly in advance</li>
<li>Maintenance retainer (if retained) is invoiced monthly in advance</li>
</ul>
<h2>2.5 Payment Terms.</h2>
<p>All invoices are payable within 14 days of issue unless otherwise agreed in writing. Late payment may result in suspension of services and/or interest charges of 4% above the Bank of England base rate.</p>
<h2>2.6 Expenses.</h2>
<p>Third-party costs incurred on behalf of the Client (e.g., cloud hosting, AI API usage, domain registration) will be passed through at cost with no markup, subject to prior approval where costs exceed £100 per month.</p>

<h1>3. TERM &amp; TERMINATION</h1>
<h2>3.1 Commencement.</h2>
<p>The contract commences on the Start Date specified in the accepted proposal.</p>
<h2>3.2 Build Phase.</h2>
<p>The initial build phase runs until the handover milestone is reached, as defined in the project timeline.</p>
<h2>3.3 Transition Phase.</h2>
<p>The transition phase runs for the agreed period (typically 3–6 months) following go-live.</p>
<h2>3.4 Post-Handover.</h2>
<p>If the Client retains the optional maintenance retainer, this runs on a rolling monthly basis and may be cancelled by either party with 30 days' written notice.</p>
<h2>3.5 Termination for Convenience.</h2>
<p>Either party may terminate this agreement with 30 days' written notice, provided that:</p>
<ul>
<li>If terminated during the build phase, the Client remains liable for all work completed to date plus 50% of outstanding milestones</li>
<li>If terminated during transition or post-handover phases, fees for services rendered remain payable</li>
</ul>
<h2>3.6 Termination for Cause.</h2>
<p>Either party may terminate immediately if the other party:</p>
<ul>
<li>Commits a material breach and fails to remedy within 14 days of written notice</li>
<li>Becomes insolvent, enters administration, or ceases trading</li>
<li>Fails to make payment within 30 days of the due date</li>
</ul>
<h2>3.7 Effect of Termination.</h2>
<p>Upon termination:</p>
<ul>
<li>All outstanding fees become immediately payable</li>
<li>The Client retains access to all work completed and paid for to date</li>
<li>Beyond Measure will provide reasonable assistance to facilitate transition to another provider (at standard hourly rates if outside the scope of services already paid for)</li>
<li>Confidential information obligations continue indefinitely</li>
</ul>

<h1>4. INTELLECTUAL PROPERTY &amp; OWNERSHIP</h1>
<h2>4.1 Source Code Ownership.</h2>
<p>The underlying source code, architecture, frameworks, libraries and proprietary development tools used to build the Platform remain the intellectual property of Beyond Measure Ltd. The Client does not acquire ownership of, or rights to, the source code.</p>
<h2>4.2 Deployment License.</h2>
<p>The Client receives a non-exclusive, non-transferable, perpetual license to USE the deployed Platform for their internal business operations. This license includes the right to:</p>
<ul>
<li>Operate the Platform on their designated infrastructure</li>
<li>Modify configurations, branding, and user-facing content via administrative interfaces</li>
<li>Access and export all data stored within the Platform</li>
</ul>
<h2>4.3 Restrictions.</h2>
<p>The Client may not:</p>
<ul>
<li>Reverse-engineer, decompile, or disassemble the Platform source code</li>
<li>Resell, sublicense, white-label or distribute the Platform to third parties</li>
<li>Use the Platform to provide commercial services to third parties (unless this is the Platform's intended purpose as documented in the Statement of Work)</li>
<li>Create derivative works based on the source code for commercial distribution</li>
</ul>
<h2>4.4 Reuse by Beyond Measure.</h2>
<p>Beyond Measure retains the right to reuse components, architecture patterns, and non-Client-specific functionality developed during this engagement for other clients. Client-specific configurations, branding, data structures and business logic unique to the Client will not be reused without written consent.</p>
<h2>4.5 Portfolio Rights.</h2>
<p>Beyond Measure may reference the Client and the Platform in case studies, proposals and marketing materials, subject to the Client's prior written approval of specific content. General references (e.g., "we built a risk management platform for a health &amp; safety consultancy") do not require approval.</p>
<h2>4.6 Third-Party Components.</h2>
<p>The Platform may incorporate open-source libraries and third-party components licensed under permissive licenses (MIT, Apache 2.0, BSD, etc.). The Client's use of these components is subject to the terms of those licenses. Beyond Measure warrants it has the right to use all incorporated components.</p>

<h1>5. DATA OWNERSHIP &amp; PROTECTION</h1>
<h2>5.1 Client Data Ownership.</h2>
<p>The Client retains full ownership of all data entered into, generated by, or stored within the Platform ("Client Data"). This includes but is not limited to: user records, project data, documents, audit logs, reports and any information uploaded by the Client or their end-users.</p>
<h2>5.2 Data Processing.</h2>
<p>Beyond Measure acts as a data processor on behalf of the Client. Where the Client processes personal data of individuals (e.g., employees, contractors, clients), the Client is the data controller and Beyond Measure is the data processor under UK GDPR.</p>
<h2>5.3 Data Processing Addendum.</h2>
<p>The parties agree to execute a Data Processing Addendum (DPA) in a form compliant with UK GDPR, which forms part of this agreement. The DPA will govern the processing of personal data and set out:</p>
<ul>
<li>The subject matter, duration, nature and purpose of processing</li>
<li>Types of personal data and categories of data subjects</li>
<li>Security measures and sub-processor arrangements</li>
<li>Obligations regarding data subject rights, breach notification and data return/deletion</li>
</ul>
<h2>5.4 Security Measures.</h2>
<p>Beyond Measure will apply commercially reasonable technical and organisational security measures to protect Client Data, including:</p>
<ul>
<li>Encryption of data in transit (TLS 1.2+) and at rest (AES-256 or equivalent)</li>
<li>Role-based access controls and authentication mechanisms</li>
<li>Regular security updates and patches to infrastructure and dependencies</li>
<li>Secure development practices (code review, dependency scanning, penetration testing where appropriate)</li>
<li>Automated backups with tested restore procedures</li>
</ul>
<h2>5.5 Data Location.</h2>
<p>Client Data will be stored in data centres located in the United Kingdom and/or European Economic Area unless otherwise agreed in writing. If the Client requests deployment to non-UK/EEA regions, the Client acknowledges responsibility for compliance with applicable data protection laws.</p>
<h2>5.6 Data Retention &amp; Deletion.</h2>
<p>Upon termination or at the Client's written request:</p>
<ul>
<li>Beyond Measure will provide the Client with a full export of Client Data in machine-readable formats (CSV, JSON, SQL dump as appropriate)</li>
<li>Beyond Measure will securely delete all Client Data from production systems within 30 days, unless legally required to retain (e.g., for accounting purposes)</li>
<li>Backup copies may persist for up to 90 days in accordance with standard backup retention policies</li>
</ul>
<h2>5.7 Data Breach Notification.</h2>
<p>In the event of a personal data breach affecting Client Data, Beyond Measure will notify the Client without undue delay and in any event within 72 hours of becoming aware of the breach. Notification will include available details of the breach, likely consequences, and mitigation measures taken or proposed.</p>
<h2>5.8 Sub-Processors.</h2>
<p>Beyond Measure may engage sub-processors (e.g., cloud infrastructure providers, AI API providers) to assist in delivering the services. A list of current sub-processors will be provided to the Client on request. Beyond Measure will:</p>
<ul>
<li>Conduct due diligence on sub-processors' security and compliance posture</li>
<li>Ensure sub-processors are bound by data protection obligations no less protective than those in this agreement</li>
<li>Notify the Client of any changes to sub-processors with reasonable advance notice (minimum 30 days)</li>
<li>Provide the Client with the opportunity to object to new sub-processors on reasonable grounds</li>
</ul>

<h1>6. CONFIDENTIALITY &amp; NON-DISCLOSURE</h1>
<h2>6.1 Confidential Information.</h2>
<p>Each party ("Receiving Party") agrees to keep confidential all information disclosed by the other party ("Disclosing Party") that is:</p>
<ul>
<li>Marked as "Confidential" or reasonably understood to be confidential given its nature</li>
<li>Relating to business operations, customers, pricing, technical architecture, trade secrets, or proprietary processes</li>
</ul>
<h2>6.2 Examples of Confidential Information:</h2>
<p><strong>Beyond Measure's Confidential Information:</strong> Source code, development methodologies, pricing structures, technical architecture documentation, proprietary tools</p>
<p><strong>Client's Confidential Information:</strong> Business plans, client lists, financial data, internal processes, project data, any data entered into the Platform</p>
<h2>6.3 Obligations.</h2>
<p>The Receiving Party will:</p>
<ul>
<li>Use Confidential Information solely to perform obligations or exercise rights under this agreement</li>
<li>Disclose Confidential Information only to employees, contractors or advisors who need to know and who are bound by confidentiality obligations</li>
<li>Protect Confidential Information with the same degree of care used for its own confidential information (but no less than reasonable care)</li>
<li>Not disclose Confidential Information to third parties without prior written consent</li>
</ul>
<h2>6.4 Exceptions.</h2>
<p>Confidentiality obligations do not apply to information that:</p>
<ul>
<li>Was publicly known at the time of disclosure or becomes publicly known through no breach by the Receiving Party</li>
<li>Was rightfully known by the Receiving Party prior to disclosure</li>
<li>Is independently developed by the Receiving Party without reference to the Confidential Information</li>
<li>Is required to be disclosed by law, regulation or court order (provided the Receiving Party gives reasonable advance notice where permitted)</li>
</ul>
<h2>6.5 Duration.</h2>
<p>Confidentiality obligations survive termination of this agreement and continue indefinitely, except that obligations regarding trade secrets continue for as long as the information remains a trade secret under applicable law.</p>
<h2>6.6 Return of Confidential Information.</h2>
<p>Upon termination or at the Disclosing Party's request, the Receiving Party will:</p>
<ul>
<li>Return or securely destroy all Confidential Information in tangible form</li>
<li>Delete all Confidential Information from electronic systems (subject to backup retention policies and legal obligations)</li>
<li>Provide written certification of compliance upon request</li>
</ul>

<h1>7. SUPPORT &amp; MAINTENANCE</h1>
<h2>7.1 During Build &amp; Transition.</h2>
<p>Throughout the build and transition phases, Beyond Measure provides:</p>
<ul>
<li>Email and video call support during UK business hours (Monday–Friday, 09:00–17:30 GMT/BST, excluding bank holidays)</li>
<li>Bug fixes and issue resolution</li>
<li>Iterative refinement based on user feedback</li>
<li>Training and documentation</li>
</ul>
<h2>7.2 Post-Handover Support (Optional).</h2>
<p>If the Client retains the optional maintenance retainer, Beyond Measure provides:</p>
<ul>
<li>Infrastructure monitoring and uptime management</li>
<li>Database backups and maintenance</li>
<li>Security patches and dependency updates</li>
<li>AI API cost management and usage monitoring</li>
<li>Bug fixes and minor adjustments (up to 2 hours/month included; additional work at standard hourly rates)</li>
<li>Email/Slack support with 4-hour response time on critical issues, 24-hour on non-critical</li>
</ul>
<h2>7.3 Exclusions.</h2>
<p>Support does not include:</p>
<ul>
<li>Issues caused by Client modifications to infrastructure or configurations outside provided interfaces</li>
<li>Third-party software or services not managed by Beyond Measure</li>
<li>Training for new users beyond initial onboarding (available at standard hourly rates)</li>
<li>Feature development (available under separate feature development retainer)</li>
</ul>
<h2>7.4 Service Level Targets.</h2>
<p>Beyond Measure will use commercially reasonable efforts to achieve:</p>
<ul>
<li>99% uptime for Client-hosted infrastructure we manage (excludes Client-caused outages, scheduled maintenance, force majeure)</li>
<li>&lt; 4 hour response time on critical issues (platform down, data loss, security breach)</li>
<li>&lt; 24 hour response time on non-critical issues</li>
</ul>
<h2>7.5 Escalation.</h2>
<p>Critical issues can be escalated via email to support@beyondmeasure.global or via phone (number provided on contract signature).</p>

<h1>8. WARRANTIES &amp; DISCLAIMERS</h1>
<h2>8.1 Beyond Measure Warranties.</h2>
<p>Beyond Measure warrants that:</p>
<ul>
<li>Services will be performed in a professional and workmanlike manner consistent with industry standards</li>
<li>Beyond Measure has the right to grant the licenses set out in this agreement</li>
<li>The Platform will materially conform to the agreed specification at the time of handover</li>
<li>Beyond Measure will comply with applicable data protection laws in its role as data processor</li>
</ul>
<h2>8.2 Client Warranties.</h2>
<p>The Client warrants that:</p>
<ul>
<li>It has authority to enter into this agreement and grant Beyond Measure access to systems, data and personnel as necessary</li>
<li>Client Data does not violate any third-party intellectual property rights, confidentiality obligations or applicable laws</li>
<li>It will use the Platform in compliance with applicable laws and regulations</li>
</ul>
<h2>8.3 Disclaimer.</h2>
<p>EXCEPT AS EXPRESSLY SET OUT IN THIS AGREEMENT, BEYOND MEASURE MAKES NO WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. The Platform is provided "as is" once handed over.</p>
<h2>8.4 No Guarantee of Outcomes.</h2>
<p>Beyond Measure does not guarantee specific business outcomes, cost savings, revenue increases, or performance metrics resulting from use of the Platform.</p>

<h1>9. LIMITATION OF LIABILITY</h1>
<h2>9.1 Liability Cap.</h2>
<p>Beyond Measure's total aggregate liability arising out of or related to this agreement, whether in contract, tort (including negligence), breach of statutory duty or otherwise, is limited to the total fees paid by the Client in the 12 months preceding the claim (or if less than 12 months have elapsed, the total fees paid to date).</p>
<h2>9.2 Excluded Losses.</h2>
<p>Beyond Measure will not be liable for:</p>
<ul>
<li>Loss of profits, revenue, business, contracts, or anticipated savings</li>
<li>Loss of or corruption to data (except to the extent caused by Beyond Measure's gross negligence)</li>
<li>Indirect, consequential, special or punitive damages</li>
<li>Losses caused by Client's breach, misuse of the Platform, or failure to follow documentation</li>
</ul>
<h2>9.3 Exceptions to Liability Cap.</h2>
<p>Nothing in this agreement excludes or limits liability for:</p>
<ul>
<li>Death or personal injury caused by negligence</li>
<li>Fraud or fraudulent misrepresentation</li>
<li>Breach of confidentiality obligations</li>
<li>Infringement of intellectual property rights</li>
<li>Any other liability that cannot be excluded or limited under applicable law</li>
</ul>
<h2>9.4 Client Indemnity.</h2>
<p>The Client will indemnify and hold Beyond Measure harmless from any claims, damages or losses arising from:</p>
<ul>
<li>Client Data or the Client's use of the Platform</li>
<li>Breach by the Client of this agreement</li>
<li>Violation of applicable laws or third-party rights by the Client</li>
</ul>

<h1>10. FORCE MAJEURE</h1>
<h2>10.1</h2>
<p>Neither party will be liable for failure to perform obligations (other than payment obligations) to the extent caused by events beyond their reasonable control, including but not limited to: acts of God, war, terrorism, civil unrest, government action, strikes, epidemics, internet or cloud provider outages, or natural disasters.</p>
<h2>10.2</h2>
<p>The affected party must promptly notify the other party and use reasonable efforts to mitigate the impact. If force majeure continues for more than 60 days, either party may terminate this agreement without penalty.</p>

<h1>11. GENERAL PROVISIONS</h1>
<h2>11.1 Entire Agreement.</h2>
<p>This agreement (together with the accepted proposal, Statement of Work, and Data Processing Addendum) constitutes the entire agreement between the parties and supersedes all prior agreements, understandings and representations.</p>
<h2>11.2 Amendments.</h2>
<p>No amendment or variation to this agreement is valid unless in writing and signed by authorised representatives of both parties.</p>
<h2>11.3 Assignment.</h2>
<p>Neither party may assign this agreement without the prior written consent of the other party, except that Beyond Measure may assign to a successor entity in the event of a merger, acquisition or sale of business.</p>
<h2>11.4 Severability.</h2>
<p>If any provision is found to be invalid or unenforceable, the remainder of the agreement remains in full force and effect.</p>
<h2>11.5 Waiver.</h2>
<p>Failure to enforce any provision does not constitute a waiver of that provision or any other provision.</p>
<h2>11.6 Notices.</h2>
<p>All notices must be in writing and sent to the addresses specified in the contract signature block. Notices are deemed received:</p>
<ul>
<li>(a) if delivered by hand, on delivery;</li>
<li>(b) if sent by email, on transmission;</li>
<li>(c) if sent by post, 3 business days after posting.</li>
</ul>
<h2>11.7 Governing Law.</h2>
<p>This agreement is governed by the laws of England and Wales. The parties submit to the exclusive jurisdiction of the courts of England and Wales.</p>
<h2>11.8 Dispute Resolution.</h2>
<p>In the event of a dispute, the parties agree to first attempt resolution through good-faith negotiation. If unresolved within 30 days, either party may escalate to mediation under CEDR rules before pursuing litigation.</p>

<h1>12. ACCEPTANCE</h1>
<h2>12.1</h2>
<p>By signing below, the Signatory confirms that:</p>
<ul>
<li>They have authority to bind the Client to this agreement</li>
<li>They have read, understood and accept these Terms &amp; Conditions</li>
<li>They accept the commercial option selected in the proposal</li>
<li>They consent to Beyond Measure processing personal data in accordance with the Data Processing Addendum</li>
</ul>
<h2>12.2</h2>
<p>This agreement may be executed in counterparts (including electronic signatures), each of which constitutes an original and all of which together constitute one agreement.</p>`;
